import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Topbar from "../components/layout/Topbar.jsx";
import { getCompanies } from "../services/companyService.js";
import {
  adminExportVault,
  downloadVaultFile,
  adminGetVaultSettings,
  adminSaveVaultSettings,
  adminCreateVaultShare,
  adminGetVaultShares,
  adminRevokeShare,
} from "../services/credentialService.js";

const SHARE_STATUS_META = {
  ACTIVE:  { label: "Actif",    color: "#2F9E44", bg: "rgba(47,158,68,.1)"  },
  VIEWED:  { label: "Consulté", color: "#6B7280", bg: "rgba(107,114,128,.1)" },
  EXPIRED: { label: "Expiré",   color: "#F08C00", bg: "rgba(240,140,0,.1)"  },
  REVOKED: { label: "Révoqué",  color: "#DC2626", bg: "rgba(220,38,38,.1)"  },
};

/**
 * Page dédiée — Coffre de secours hors-ligne.
 *
 * Regroupe tout ce qui touche l'export chiffré des mots de passe, pour
 * toutes les entreprises, au même endroit (auparavant : un onglet "Sécurité"
 * répété sur chaque fiche entreprise) :
 *  - Téléchargement manuel : une entreprise, une sélection, ou toutes
 *    confondues dans le MÊME fichier, au format .dcv (lecteur autonome
 *    vault-reader.html) ou .xlsx protégé par mot de passe.
 *  - Génération automatique nocturne : reste configurée par entreprise
 *    (chaque .dcv nocturne est un fichier séparé sur le NAS), mais toutes
 *    les entreprises sont listées ici plutôt que fiche par fiche.
 *
 * Accessible via ?companyId=... (lien "Gérer le coffre de secours" depuis
 * ClientDetailPage) pour présélectionner une entreprise au lieu de "Toutes".
 */

// Même déduplication que ClientsPage.jsx (entreprises quasi-doublons issues
// de la synchronisation Atera) — répétée ici pour ne pas proposer deux fois
// la même entreprise dans la sélection d'export.
function normalizeName(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[-_]/g, " ")
    .replace(/[éèêë]/g, "e")
    .replace(/[àâä]/g, "a")
    .replace(/[ùûü]/g, "u")
    .replace(/[îï]/g, "i")
    .replace(/[ôö]/g, "o")
    .replace(/[ç]/g, "c")
    .replace(/\s+/g, " ")
    .trim();
}
function dedupeCompanies(raw) {
  const seen = new Map();
  raw.forEach((c) => {
    const key = normalizeName(c.name);
    if (!seen.has(key)) {
      seen.set(key, c);
    } else {
      const existing = seen.get(key);
      if (!existing.ateraCustomerId && c.ateraCustomerId) seen.set(key, c);
    }
  });
  return [...seen.values()];
}

export default function VaultExportPage() {
  const [searchParams] = useSearchParams();
  const preselectId = searchParams.get("companyId");

  // ── Entreprises ──────────────────────────────
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ── Export manuel ────────────────────────────
  const [selectAll, setSelectAll] = useState(!preselectId);
  const [selectedIds, setSelectedIds] = useState(() => (preselectId ? [preselectId] : []));
  const [search, setSearch] = useState("");
  const [format, setFormat] = useState("DCV"); // "DCV" | "XLSX"
  const [passphrase, setPassphrase] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  // ── Génération automatique par entreprise ────
  const [autoSettings, setAutoSettings] = useState({}); // { [companyId]: { vaultAutoExportEnabled, hasPassphrase } }
  const [autoLoading, setAutoLoading] = useState(true);
  const [editingAutoId, setEditingAutoId] = useState(null);
  const [autoPassphrase, setAutoPassphrase] = useState("");
  const [showAutoPwd, setShowAutoPwd] = useState(false);
  const [savingAutoId, setSavingAutoId] = useState(null);
  const [autoError, setAutoError] = useState("");

  // ── Partage groupé du coffre (une entreprise à la fois) ──
  const [shareCompanyId, setShareCompanyId] = useState(preselectId || "");
  const [shareDuration, setShareDuration] = useState(24); // 1 | 24 | 72
  const [shareIncludeNotes, setShareIncludeNotes] = useState(false);
  const [sharePassphrase, setSharePassphrase] = useState("");
  const [showSharePwd, setShowSharePwd] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState("");
  const [newShareLink, setNewShareLink] = useState(null); // { token, shareUrl, expiresAt, requiresPassphrase }
  const [shareCopied, setShareCopied] = useState(false);
  const [shareHistory, setShareHistory] = useState([]);
  const [shareHistoryLoading, setShareHistoryLoading] = useState(false);
  const [revokingShareToken, setRevokingShareToken] = useState(null);

  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCompanies = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getCompanies();
      const list = dedupeCompanies(res.data || []);
      setCompanies(list);
      loadAutoSettings(list);
      // Présélectionne une entreprise pour le partage groupé dès que la
      // liste est disponible (celle passée en ?companyId=, sinon la première).
      setShareCompanyId((cur) => cur || preselectId || list[0]?.id || "");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAutoSettings = async (list) => {
    setAutoLoading(true);
    try {
      const entries = await Promise.all(
        list.map(async (c) => {
          try {
            const s = await adminGetVaultSettings(c.id);
            return [c.id, s];
          } catch {
            return [c.id, null];
          }
        })
      );
      setAutoSettings(Object.fromEntries(entries));
    } finally {
      setAutoLoading(false);
    }
  };

  const filtered = useMemo(
    () => companies.filter((c) => (c.name || "").toLowerCase().includes(search.toLowerCase())),
    [companies, search]
  );

  const toggleCompany = (companyId) => {
    setSelectAll(false);
    setSelectedIds((prev) =>
      prev.includes(companyId) ? prev.filter((x) => x !== companyId) : [...prev, companyId]
    );
  };

  // ── Export manuel ────────────────────────────
  const handleExport = async () => {
    setExportError("");
    if (passphrase.length < 12) {
      setExportError("La phrase secrète doit contenir au moins 12 caractères.");
      return;
    }
    if (!selectAll && selectedIds.length === 0) {
      setExportError('Sélectionnez au moins une entreprise (ou cochez "Toutes les entreprises").');
      return;
    }
    setExporting(true);
    try {
      const companyIds = selectAll ? [] : selectedIds;
      const result = await adminExportVault({ companyIds, passphrase, format });

      const label = selectAll
        ? "toutes-entreprises"
        : selectedIds.length === 1
        ? (companies.find((c) => c.id === selectedIds[0])?.name || "entreprise").replace(/[^a-zA-Z0-9_-]/g, "_")
        : `${selectedIds.length}-entreprises`;
      const ext = format === "XLSX" ? "xlsx" : "dcv";
      downloadVaultFile(result, `coffre-de-secours_${label}.${ext}`);
      setPassphrase("");
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExporting(false);
    }
  };

  // ── Partage groupé du coffre ─────────────────
  const loadShareHistory = async (companyId) => {
    if (!companyId) {
      setShareHistory([]);
      return;
    }
    setShareHistoryLoading(true);
    try {
      const res = await adminGetVaultShares(companyId);
      setShareHistory(Array.isArray(res) ? res : (res?.data ?? []));
    } catch {
      // Non bloquant — l'historique est un plus, pas indispensable à la création.
    } finally {
      setShareHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (shareCompanyId) loadShareHistory(shareCompanyId);
    setNewShareLink(null);
    setShareError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareCompanyId]);

  const handleCreateShare = async () => {
    setShareError("");
    if (!shareCompanyId) {
      setShareError("Sélectionnez une entreprise.");
      return;
    }
    if (sharePassphrase && sharePassphrase.length < 12) {
      setShareError("La phrase secrète doit contenir au moins 12 caractères (ou laissez-la vide).");
      return;
    }
    setSharing(true);
    try {
      const link = await adminCreateVaultShare({
        companyId: shareCompanyId,
        durationHours: shareDuration,
        includeNotes: shareIncludeNotes,
        passphrase: sharePassphrase || undefined,
      });
      setNewShareLink(link);
      setSharePassphrase("");
      loadShareHistory(shareCompanyId);
    } catch (err) {
      setShareError(err.message);
    } finally {
      setSharing(false);
    }
  };

  const copyShareLink = async () => {
    if (!newShareLink?.shareUrl) return;
    await navigator.clipboard.writeText(newShareLink.shareUrl);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  const handleRevokeShare = async (token) => {
    setRevokingShareToken(token);
    try {
      await adminRevokeShare(token);
      loadShareHistory(shareCompanyId);
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setRevokingShareToken(null);
    }
  };

  // ── Génération automatique ───────────────────
  const handleToggleAuto = async (companyId, checked) => {
    setAutoError("");

    if (!checked) {
      setSavingAutoId(companyId);
      try {
        await adminSaveVaultSettings(companyId, { vaultAutoExportEnabled: false });
        setAutoSettings((prev) => ({ ...prev, [companyId]: { vaultAutoExportEnabled: false, hasPassphrase: false } }));
        setEditingAutoId((cur) => (cur === companyId ? null : cur));
      } catch (err) {
        setAutoError(err.message);
      } finally {
        setSavingAutoId(null);
      }
      return;
    }

    const current = autoSettings[companyId];
    if (current?.hasPassphrase) {
      // Une phrase existe déjà — pas besoin d'en ressaisir une pour réactiver.
      setSavingAutoId(companyId);
      try {
        await adminSaveVaultSettings(companyId, { vaultAutoExportEnabled: true });
        setAutoSettings((prev) => ({ ...prev, [companyId]: { ...prev[companyId], vaultAutoExportEnabled: true } }));
      } catch (err) {
        setAutoError(err.message);
      } finally {
        setSavingAutoId(null);
      }
    } else {
      // Pas encore de phrase enregistrée : on affiche le champ, l'activation
      // réelle n'est envoyée qu'au clic sur "Activer".
      setEditingAutoId(companyId);
      setAutoPassphrase("");
    }
  };

  const handleSaveAutoPassphrase = async (companyId) => {
    setAutoError("");
    if (autoPassphrase.length < 12) {
      setAutoError("La phrase secrète doit contenir au moins 12 caractères.");
      return;
    }
    setSavingAutoId(companyId);
    try {
      await adminSaveVaultSettings(companyId, { vaultAutoExportEnabled: true, passphrase: autoPassphrase });
      setAutoSettings((prev) => ({ ...prev, [companyId]: { vaultAutoExportEnabled: true, hasPassphrase: true } }));
      setEditingAutoId(null);
      setAutoPassphrase("");
    } catch (err) {
      setAutoError(err.message);
    } finally {
      setSavingAutoId(null);
    }
  };

  return (
    <>
      <Topbar title="Coffre de" subtitle="secours" />
      <div className="page-body">
        <div className="page-header">
          <div>
            <div className="page-title">
              Coffre de <span>secours</span>
            </div>
            <div className="page-sub">Export chiffré des mots de passe — téléchargement manuel et génération automatique</div>
          </div>
        </div>

        {/* ── Téléchargement manuel ── */}
        <div className="itm-card anim-fade-up" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
            <i className="bi bi-download me-2" />
            Téléchargement manuel
          </div>
          <div style={{ fontSize: 12.5, color: "var(--text-3)", marginBottom: 16, lineHeight: 1.6 }}>
            Sélectionnez une ou plusieurs entreprises (ou toutes, réunies dans le même fichier),
            choisissez le format, puis saisissez une phrase secrète — elle n'est jamais
            enregistrée, c'est le seul moyen de rouvrir le fichier ensuite.
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: 30, color: "var(--text-3)" }}>
              <i className="bi bi-arrow-repeat spin" style={{ fontSize: 22, opacity: 0.4 }} />
            </div>
          ) : error ? (
            <div style={{ color: "var(--danger)", fontSize: 13 }}>{error}</div>
          ) : (
            <>
              {/* Sélection des entreprises */}
              <div style={{ marginBottom: 16 }}>
                <label
                  style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", marginBottom: 10 }}
                >
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => {
                      setSelectAll(e.target.checked);
                      if (e.target.checked) setSelectedIds([]);
                    }}
                  />
                  Toutes les entreprises ({companies.length})
                </label>

                {!selectAll && (
                  <div style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 10 }}>
                    <input
                      className="itm-input"
                      style={{ marginBottom: 8 }}
                      placeholder="Rechercher une entreprise…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
                      {filtered.map((c) => (
                        <label
                          key={c.id}
                          style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "5px 6px", borderRadius: 6, cursor: "pointer" }}
                        >
                          <input type="checkbox" checked={selectedIds.includes(c.id)} onChange={() => toggleCompany(c.id)} />
                          {c.name}
                        </label>
                      ))}
                      {filtered.length === 0 && (
                        <div style={{ fontSize: 12.5, color: "var(--text-3)", padding: 6 }}>Aucune entreprise trouvée.</div>
                      )}
                    </div>
                    {selectedIds.length > 0 && (
                      <div style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 6 }}>
                        {selectedIds.length} entreprise{selectedIds.length > 1 ? "s" : ""} sélectionnée
                        {selectedIds.length > 1 ? "s" : ""}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Format */}
              <div style={{ marginBottom: 16 }}>
                <label className="itm-label" style={{ display: "block", marginBottom: 6 }}>
                  Format
                </label>
                <div style={{ display: "flex", gap: 8, maxWidth: 440 }}>
                  {[
                    { id: "DCV", label: ".dcv (lecteur autonome)" },
                    { id: "XLSX", label: ".xlsx protégé par mot de passe" },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setFormat(opt.id)}
                      style={{
                        flex: 1,
                        padding: "8px 10px",
                        borderRadius: 8,
                        fontSize: 12.5,
                        fontWeight: 600,
                        border: format === opt.id ? "2px solid var(--accent)" : "1px solid var(--border)",
                        background: format === opt.id ? "rgba(230,51,41,.08)" : "transparent",
                        color: format === opt.id ? "var(--accent)" : "var(--text-2)",
                        cursor: "pointer",
                        transition: "all .12s",
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {exportError && (
                <div
                  style={{
                    background: "var(--danger-dim)",
                    border: "1px solid rgba(239,68,68,.25)",
                    borderRadius: 8,
                    color: "var(--danger)",
                    fontSize: 12,
                    padding: "8px 12px",
                    marginBottom: 12,
                  }}
                >
                  <i className="bi bi-exclamation-triangle me-2" />
                  {exportError}
                </div>
              )}

              <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div className="form-group" style={{ flex: 1, minWidth: 220, marginBottom: 0 }}>
                  <label className="itm-label">Phrase secrète (min. 12 caractères)</label>
                  <div style={{ position: "relative" }}>
                    <input
                      className="itm-input"
                      style={{ paddingRight: 42 }}
                      type={showPwd ? "text" : "password"}
                      value={passphrase}
                      onChange={(e) => setPassphrase(e.target.value)}
                      placeholder="Phrase secrète du coffre"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      style={{
                        position: "absolute",
                        right: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--text-3)",
                      }}
                    >
                      <i className={`bi ${showPwd ? "bi-eye-slash" : "bi-eye"}`} />
                    </button>
                  </div>
                </div>
                <button className="btn-itm btn-itm-primary" onClick={handleExport} disabled={exporting}>
                  {exporting ? (
                    <>
                      <i className="bi bi-arrow-repeat spin me-1" />
                      Génération…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-download me-1" />
                      Télécharger le coffre
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        {/* ── Partage groupé du coffre ── */}
        <div className="itm-card anim-fade-up" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
            <i className="bi bi-share me-2" />
            Partager le coffre d'une entreprise
          </div>
          <div style={{ fontSize: 12.5, color: "var(--text-3)", marginBottom: 16, lineHeight: 1.6 }}>
            Même principe que le partage individuel d'un mot de passe : un lien à usage
            unique, sans compte requis pour le destinataire, qui devient inutilisable dès
            qu'il a été ouvert une fois ou à l'expiration — selon la première des deux
            échéances. Ici, le lien donne accès à TOUT le coffre d'une seule entreprise —
            vous pouvez en plus exiger une phrase secrète, vu l'enjeu plus large qu'un
            partage individuel.
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: 30, color: "var(--text-3)" }}>
              <i className="bi bi-arrow-repeat spin" style={{ fontSize: 22, opacity: 0.4 }} />
            </div>
          ) : (
            <>
              {/* Entreprise (une seule à la fois) */}
              <div className="form-group" style={{ marginBottom: 16, maxWidth: 360 }}>
                <label className="itm-label">Entreprise</label>
                <select
                  className="itm-input"
                  value={shareCompanyId}
                  onChange={(e) => setShareCompanyId(e.target.value)}
                >
                  {companies.length === 0 && <option value="">Aucune entreprise</option>}
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Durée */}
              <div style={{ marginBottom: 16 }}>
                <label className="itm-label" style={{ display: "block", marginBottom: 6 }}>
                  Durée de validité
                </label>
                <div style={{ display: "flex", gap: 8, maxWidth: 440 }}>
                  {[1, 24, 72].map((h) => (
                    <button
                      key={h}
                      type="button"
                      onClick={() => setShareDuration(h)}
                      style={{
                        flex: 1,
                        padding: "8px 10px",
                        borderRadius: 8,
                        fontSize: 12.5,
                        fontWeight: 600,
                        border: shareDuration === h ? "2px solid var(--accent)" : "1px solid var(--border)",
                        background: shareDuration === h ? "rgba(230,51,41,.08)" : "transparent",
                        color: shareDuration === h ? "var(--accent)" : "var(--text-2)",
                        cursor: "pointer",
                        transition: "all .12s",
                      }}
                    >
                      {h === 1 ? "1 heure" : h === 24 ? "24 heures" : "72 heures"}
                    </button>
                  ))}
                </div>
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", marginBottom: 16 }}>
                <input type="checkbox" checked={shareIncludeNotes} onChange={(e) => setShareIncludeNotes(e.target.checked)} />
                Inclure les notes de chaque credential dans le lien
              </label>

              {shareError && (
                <div
                  style={{
                    background: "var(--danger-dim)",
                    border: "1px solid rgba(239,68,68,.25)",
                    borderRadius: 8,
                    color: "var(--danger)",
                    fontSize: 12,
                    padding: "8px 12px",
                    marginBottom: 12,
                  }}
                >
                  <i className="bi bi-exclamation-triangle me-2" />
                  {shareError}
                </div>
              )}

              {!newShareLink ? (
                <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                  <div className="form-group" style={{ flex: 1, minWidth: 220, marginBottom: 0 }}>
                    <label className="itm-label">Phrase secrète optionnelle</label>
                    <div style={{ position: "relative" }}>
                      <input
                        className="itm-input"
                        style={{ paddingRight: 42 }}
                        type={showSharePwd ? "text" : "password"}
                        value={sharePassphrase}
                        onChange={(e) => setSharePassphrase(e.target.value)}
                        placeholder="Laissez vide pour lien seul, sans phrase"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSharePwd((v) => !v)}
                        style={{
                          position: "absolute",
                          right: 10,
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "var(--text-3)",
                        }}
                      >
                        <i className={`bi ${showSharePwd ? "bi-eye-slash" : "bi-eye"}`} />
                      </button>
                    </div>
                  </div>
                  <button className="btn-itm btn-itm-primary" onClick={handleCreateShare} disabled={sharing || !shareCompanyId}>
                    {sharing ? (
                      <>
                        <i className="bi bi-arrow-repeat spin me-1" />
                        Création…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-link-45deg me-1" />
                        Créer le lien
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#2F9E44", marginBottom: 8 }}>
                    <i className="bi bi-check-circle me-1" />
                    Lien créé — usage unique{newShareLink.requiresPassphrase ? ", protégé par phrase secrète" : ""}
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", background: "var(--bg-2, #F9FAFB)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 10px" }}>
                    <span style={{ fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                      {newShareLink.shareUrl}
                    </span>
                    <button
                      type="button"
                      onClick={copyShareLink}
                      title="Copier"
                      style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)" }}
                    >
                      <i className={`bi ${shareCopied ? "bi-check2" : "bi-copy"}`} style={{ color: shareCopied ? "#69DB7C" : undefined }} />
                    </button>
                  </div>
                  <p style={{ fontSize: 12, color: "var(--text-3)", marginTop: 8 }}>
                    Ce lien ne sera plus jamais affiché — copiez-le et envoyez-le au
                    destinataire dès maintenant
                    {newShareLink.requiresPassphrase ? ", ainsi que la phrase secrète, par un canal séparé." : "."}
                  </p>
                  <button
                    type="button"
                    onClick={() => { setNewShareLink(null); setShareError(""); }}
                    style={{ fontSize: 12.5, color: "var(--accent)", background: "none", border: "none", cursor: "pointer", padding: 0, marginTop: 4 }}
                  >
                    Créer un autre lien
                  </button>
                </div>
              )}

              {/* Historique des partages groupés pour l'entreprise sélectionnée */}
              <div style={{ borderTop: "1px solid var(--border)", marginTop: 16, paddingTop: 14 }}>
                <label className="itm-label" style={{ display: "block", marginBottom: 8 }}>
                  Historique des partages de cette entreprise
                </label>
                {shareHistoryLoading ? (
                  <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>Chargement…</div>
                ) : shareHistory.length === 0 ? (
                  <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>Aucun lien créé pour cette entreprise.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto" }}>
                    {shareHistory.map((s) => {
                      const meta = SHARE_STATUS_META[s.status] || SHARE_STATUS_META.EXPIRED;
                      return (
                        <div key={s.token} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "6px 8px", borderRadius: 8, background: "var(--bg-2, #F9FAFB)" }}>
                          <span style={{ fontWeight: 600, color: meta.color, background: meta.bg, borderRadius: 20, padding: "2px 8px", fontSize: 11 }}>
                            {meta.label}
                          </span>
                          {s.requiresPassphrase && (
                            <i className="bi bi-shield-lock" title="Protégé par phrase secrète" style={{ color: "var(--text-3)" }} />
                          )}
                          <span style={{ color: "var(--text-3)", flex: 1 }}>
                            Créé le {s.createdAt ? new Date(s.createdAt).toLocaleString("fr-FR") : "—"}
                            {s.createdBy ? ` par ${s.createdBy}` : ""}
                          </span>
                          {s.status === "ACTIVE" && (
                            <button
                              type="button"
                              onClick={() => handleRevokeShare(s.token)}
                              disabled={revokingShareToken === s.token}
                              style={{ fontSize: 11, color: "var(--danger)", background: "none", border: "1px solid rgba(239,68,68,.3)", borderRadius: 6, padding: "3px 8px", cursor: "pointer" }}
                            >
                              {revokingShareToken === s.token ? "…" : "Révoquer"}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── Génération automatique nocturne ── */}
        <div className="itm-card anim-fade-up" style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
            <i className="bi bi-arrow-repeat me-2" />
            Génération automatique nocturne
          </div>
          <div style={{ fontSize: 12.5, color: "var(--text-3)", marginBottom: 16, lineHeight: 1.6 }}>
            Un fichier .dcv par entreprise activée, régénéré chaque nuit et déposé sur le NAS —
            indépendant du téléchargement manuel ci-dessus (pas de format .xlsx ici, et pas
            d'historique conservé, contrairement au backup de la base de données).
          </div>

          {autoError && (
            <div
              style={{
                background: "var(--danger-dim)",
                border: "1px solid rgba(239,68,68,.25)",
                borderRadius: 8,
                color: "var(--danger)",
                fontSize: 12,
                padding: "8px 12px",
                marginBottom: 12,
              }}
            >
              <i className="bi bi-exclamation-triangle me-2" />
              {autoError}
            </div>
          )}

          {autoLoading ? (
            <div style={{ textAlign: "center", padding: 30, color: "var(--text-3)" }}>
              <i className="bi bi-arrow-repeat spin" style={{ fontSize: 22, opacity: 0.4 }} />
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {companies.map((c) => {
                const s = autoSettings[c.id] || {};
                const isEditing = editingAutoId === c.id;
                return (
                  <div key={c.id} style={{ borderBottom: "1px solid var(--border)", padding: "10px 4px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {s.vaultAutoExportEnabled && (
                          <span style={{ fontSize: 11, color: "#2F9E44", background: "rgba(47,158,68,.1)", borderRadius: 20, padding: "2px 8px" }}>
                            Actif
                          </span>
                        )}
                        <label
                          style={{
                            position: "relative",
                            display: "inline-block",
                            width: 38,
                            height: 22,
                            flexShrink: 0,
                            cursor: savingAutoId === c.id ? "default" : "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!!s.vaultAutoExportEnabled}
                            disabled={savingAutoId === c.id}
                            onChange={(e) => handleToggleAuto(c.id, e.target.checked)}
                            style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
                          />
                          <span
                            style={{
                              position: "absolute",
                              inset: 0,
                              borderRadius: 99,
                              transition: "background .18s",
                              background: s.vaultAutoExportEnabled ? "var(--accent)" : "rgba(255,255,255,.15)",
                            }}
                          />
                          <span
                            style={{
                              position: "absolute",
                              top: 2,
                              left: s.vaultAutoExportEnabled ? 18 : 2,
                              width: 18,
                              height: 18,
                              borderRadius: "50%",
                              background: "#fff",
                              transition: "left .18s",
                            }}
                          />
                        </label>
                      </div>
                    </div>

                    {isEditing && (
                      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 8, flexWrap: "wrap" }}>
                        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
                          <input
                            className="itm-input"
                            style={{ paddingRight: 38 }}
                            type={showAutoPwd ? "text" : "password"}
                            value={autoPassphrase}
                            onChange={(e) => setAutoPassphrase(e.target.value)}
                            placeholder="Nouvelle phrase secrète (min. 12 caractères)"
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowAutoPwd((v) => !v)}
                            style={{
                              position: "absolute",
                              right: 8,
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "var(--text-3)",
                            }}
                          >
                            <i className={`bi ${showAutoPwd ? "bi-eye-slash" : "bi-eye"}`} />
                          </button>
                        </div>
                        <button
                          className="btn-itm btn-itm-primary"
                          onClick={() => handleSaveAutoPassphrase(c.id)}
                          disabled={savingAutoId === c.id}
                        >
                          {savingAutoId === c.id ? "…" : "Activer"}
                        </button>
                        <button className="btn-itm btn-itm-outline" onClick={() => setEditingAutoId(null)}>
                          Annuler
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {companies.length === 0 && <div style={{ fontSize: 12.5, color: "var(--text-3)", padding: 10 }}>Aucune entreprise.</div>}
            </div>
          )}
        </div>
      </div>
    </>
  );
}